import { useEffect, useRef, useState } from 'react';
import Question from './comps/question';
import { useQuery } from '@tanstack/react-query';
import getQuizdata from '../../services/getQuizData';
import { useTranslation } from 'react-i18next';
import convertToCSS from '../../utils/convertToCSS';
import preloadImages from '../../utils/preloadImages';
import useSubmitQuizResponse from '../../services/useSubmitQuizResponse';
import constructQuizResponse, { extractTags, getFormData } from '../../utils/constructQuizResponse';
import Loader from '../../utils/loader';
// import FeedbackModal from '../../utils/feedbackModal';
import createFontFace from '../../utils/createFontFace';
import { handleQuizInitialized, handleQuizLoad, handleQuizProgress } from '../../utils/customEvent';
import { getSessionID, postAppEvent } from '../../services/appEvent';
import formCustomEvent from '../../utils/formatCustomEvents';
import { extractHtmlText } from '../../utils/extractHtmlText';
import { addQueryParams } from '../../utils/addQueryParams';
import { preloadResourcesList } from '../../utils/preloadResources';
import fetchCustomRedirectionIfAny from '../../utils/fetchCustomRedirection';
import { getContainerId } from '../../utils/getContainerID';
import { getCustomerEmail } from '../../utils/getCustomerEmail';
import { checkAutoSubmitEmail } from '../../utils/checkAutoSubmitEmail';
import { getAutoResponse } from '../../utils/getAutoResponse';
// import { getSessionID } from '../../services/appEvent';

function Quiz() {
	const containerId = getContainerId();
	const container = document.getElementById(containerId);
	const customerEmail = getCustomerEmail();
	const { t } = useTranslation()
	// const sessionId = useRef("")
	const [sessionId, setSessionId] = useState("")
	const inAppEvent = useRef([])
	const [delayTimeOut, setDelayTimeOut] = useState(false)
	const [isQuizSubmitted, setIsQuizSubmitted] = useState(false)
	const utmSource = window.location.hostname ? `?utm_source=${encodeURIComponent(window.location.hostname)}` : '';

	const { isSuccess, isPending, isError, error, data, refetch } = useQuery({
		queryKey: ["getQuizData"],
		queryFn: () => getQuizdata(container.dataset.formid, container.dataset.shopid),
		enabled: false,
		retry: false
	})
	const [userResponse, setUserResponse] = useState<{
		questionIndex: number; questionType: string; _id: string; answerResponse: { _id: string; }[]; selfieResponse: { beta: { skinconditions: object; }; }[];
	}[]>([]);

	const { mutate, isPending: quizSubmitPending, data: submitResponse, isSuccess: quizSubmitSuccess } = useSubmitQuizResponse()

	const [currentQuestion, setCurrentQuestion] = useState(0)

	useEffect(() => {
		refetch()
	}, [refetch])

	useEffect(() => {
		if (data?.quiz?.quizRedirectDelay && isQuizSubmitted) {
			const timeoutId = setTimeout(() => setDelayTimeOut(true), Number(data.quiz.quizRedirectDelay));
			return () => clearTimeout(timeoutId); // Clean up timeout.
		}
	}, [isQuizSubmitted])

	useEffect(() => {
		//&& (!data.quiz.quizRedirectDelay || delayTimeOut)
		if (quizSubmitSuccess && (!data.quiz.quizRedirectDelay || delayTimeOut)) {
			const recoURL = data.quiz.meta.recoUrl;
			const postSubmitEvent = {
				attributes: {
					resp_id: submitResponse.resp_id
				},
				event_name: "submit_quiz_success",
				event_type: "api",
				event_timestamp: new Date().getTime(),
				page_uri: `/quiz/${data.quiz._id}/${data.questionList[data.questionList.length - 1].type === "email" ? 'form' : `question/${data.questionList[data.questionList.length - 1]._id}`}`,
			}
			postAppEvent({ sessionId: sessionId, inAppEvent: [postSubmitEvent] });

			const respId = submitResponse?.resp_id;

			const customRecoUrl = fetchCustomRedirectionIfAny(data.quiz, userResponse);
			const urlToRedirect = (customRecoUrl != null)
				? customRecoUrl
				: (recoURL && recoURL !== "/" ? recoURL : "https://a.tangent.ai/shopify-builder/app/product-recommendation.html");

			if (urlToRedirect) {
				window.location.href = addQueryParams(urlToRedirect, "resp_id", respId);
			}

		}
	}, [quizSubmitSuccess, data, submitResponse, delayTimeOut]);

	useEffect(() => {
		if (sessionId) {
			postAppEvent({ sessionId: sessionId, inAppEvent: inAppEvent.current })
			inAppEvent.current = []
		}
	}, [sessionId, inAppEvent.current])

	useEffect(() => {
		if (data) {
			preloadImages(data.questionList)
			preloadResourcesList(data?.settings?.selfieAnimation ?? false)
			const fetchSessionId = async () => {
				const sessionData = await getSessionID({ client_name: data.quiz.owner.ownerId, formId: data.quiz._id })
				setSessionId(sessionData.session_id)
			}
			fetchSessionId()
		}
	}, [data])

	useEffect(() => {
		if (isSuccess) {
			handleQuizLoad("true")
			const $style = document.createElement("style");
			const fontFace = createFontFace(data.quiz.theme?.fonts);
			let globalCSS = fontFace ? fontFace + "\n" : "";
			globalCSS += convertToCSS(data.quiz.theme.global_classes);
			// console.log(globalCSS)
			$style.innerHTML = globalCSS;
			const appContainer = document.getElementById(containerId)
			appContainer?.appendChild($style)
			saveAppEvent("pageview", "", 0)
			handleQuizInitialized(0, data.questionList.length - 1, "", data.questionList[0]._id)
		}
	}, [data?.quiz.theme.global_classes, isSuccess]);

	const saveAppEvent = (event, eventData, index) => {
		const formattedEvent = formCustomEvent(event, eventData, index, data)
		const allEvents = [...inAppEvent.current, ...formattedEvent]
		inAppEvent.current = allEvents;
	}


	const handleBackBtn = () => {
		let path = {};
		if (["selfie", "email"].includes(data.questionList[currentQuestion].type)) {
			path["from"] = data.questionList[currentQuestion].type == "email" ? `/quiz/${data.quiz._id}/form` : `/quiz/${data.quiz._id}/${data.questionList[currentQuestion].type}`;
		} else {
			path["from"] = `/quiz/${data.quiz._id}/question/${data.questionList[currentQuestion]._id}`;
		}
		for (let i = userResponse.length - 1; i >= 0; i--) {
			if (userResponse[i].questionIndex < currentQuestion) {
				setCurrentQuestion(userResponse[i].questionIndex);
				handleQuizProgress(userResponse[i].questionIndex, data.questionList.length - 1, data.questionList[currentQuestion]._id, userResponse[i]._id)
				path["to"] = ["welcome", "selfie"].includes(userResponse[i].questionType) ? `/quiz/${data.quiz._id}/${userResponse[i].questionType}` : `/quiz/${data.quiz._id}/question/${userResponse[i]._id}`;
				saveAppEvent("back", path, userResponse[i].questionIndex)
				saveAppEvent("pageview", userResponse[i], userResponse[i].questionIndex)
				break;
			}
			if (userResponse[i].questionIndex === currentQuestion && i > 0) {
				setCurrentQuestion(userResponse[i - 1].questionIndex);
				const updatedUserResponse = [...userResponse];
				updatedUserResponse.splice(i);
				setUserResponse(updatedUserResponse);
				const lastIndex = updatedUserResponse.length - 1;
				handleQuizProgress(updatedUserResponse[lastIndex].questionIndex, data.questionList.length - 1, userResponse[i]._id, updatedUserResponse[lastIndex]._id);
				path["to"] = ["welcome", "selfie"].includes(userResponse[lastIndex].questionType) ? `/quiz/${data.quiz._id}/${userResponse[lastIndex].questionType}` : `/quiz/${data.quiz._id}/question/${userResponse[lastIndex]._id}`;
				saveAppEvent("back", path, updatedUserResponse[lastIndex].questionIndex)
				saveAppEvent("pageview", updatedUserResponse[lastIndex], updatedUserResponse[lastIndex].questionIndex)
				break;
			}
		}
	}

	const findQuestionIndexById = (id: string, updatedUserResponse: Array<object>) => {
		let questionIndex = "";
		if (id != "rule") {
			questionIndex = data.questionList.findIndex((question) => question._id === id)
		} else {
			const responseTags = extractTags(updatedUserResponse)
			const checkDependancy = new Function("responseTags", data.questionList[currentQuestion].nextQuestionRule)
			const nextQuestionId = checkDependancy(responseTags)
			questionIndex = data.questionList.findIndex((question) => question._id === nextQuestionId)
		}
		if (checkAutoSubmitEmail(questionIndex, data, customerEmail)) {
			const autoResponse = getAutoResponse(questionIndex, data, customerEmail);
			questionIndex = findQuestionIndexById(autoResponse.nextQuestionId, [...updatedUserResponse, { ...autoResponse }]);
			return questionIndex;
		}
		return questionIndex;
	}

	const handleQuizSubmit = (response: { questionIndex: number; questionType: string; _id: string; answerResponse: { _id: string; }[]; selfieResponse: { beta: { skinconditions: object; }; }[]; }[]) => {
		mutate(constructQuizResponse(response, container.dataset.formid, container.dataset.shopid, data, customerEmail));
		setIsQuizSubmitted(true)
		let submitEvent = {
			attributes: {
				form: getFormData(response),
				tags: extractTags(response)
			},
			event_name: "submit_quiz",
			event_timestamp: new Date().getTime(),
			event_type: "click",
			page_uri: `/quiz/${data.quiz._id}/${data.questionList[data.questionList.length - 1].type === "email" ? 'form' : `question/${data.questionList[data.questionList.length - 1]._id}`}`,
		}
		postAppEvent({ sessionId: sessionId, inAppEvent: [submitEvent] })
	}

	const updateUserResponse = (response: {
		_id: string;
		nextQuestionId?: string;
		questionIndex?: number;
		questionType: string;
		answerResponse: { _id: string }[];
		selfieResponse: { beta: { skinconditions: object } }[];
	}) => {
		let updatedUserResponse = [...userResponse];
		const existingResponseIndex = userResponse.findIndex((responseData) => responseData._id === response._id);

		if (existingResponseIndex !== -1) {
			updatedUserResponse[existingResponseIndex] = { ...response, questionIndex: currentQuestion };
		} else {
			updatedUserResponse = [...updatedUserResponse, { ...response, questionIndex: currentQuestion }];
		}
		setUserResponse(updatedUserResponse);
		if (response.nextQuestionId === "end") {
			handleQuizSubmit(updatedUserResponse);
		} else {
			const index = findQuestionIndexById(response.nextQuestionId, updatedUserResponse);
			saveAppEvent("action", response, index)

			if (index === -1 || index >= data.questionList.length) {
				// If index is -1 or out of bounds, submit the quiz
				handleQuizSubmit(updatedUserResponse);
			} else {
				setCurrentQuestion(index);
				handleQuizProgress(index, data.questionList.length - 1, response._id, data.questionList[index]._id)
				saveAppEvent("pageview", response, index)
			}
		}
	};

	/*************************************************UI ELEMENTS********************************************************/
	if (isPending) {
		return <div className="tngnt-flex tngnt-justify-center tngnt-items-center tngnt-min-h-[300px]">
			<span className="tangent_global_style tngnt-border-solid tngnt-text-[15px]">{t('loading')}&nbsp;</span>
			<span className='tngnt-animate-bounce [animation-delay:-0.4s]'>.</span>
			<span className='tngnt-animate-bounce [animation-delay:-0.3s]'>.</span>
			<span className='tngnt-animate-bounce [animation-delay:-0.2s]'>.</span>
			<span className='tngnt-animate-bounce [animation-delay:-0.1s]'>.</span>
			<span className='tngnt-animate-bounce'>.</span>
		</div>
	}

	if (isError) {
		handleQuizLoad("false")
		return null
	}
	return (
		<>
			<div className="tangent_quiz_container_custom tangent_global_style_custom tangent_global_style_mob_custom tngnt-app-bg-color tngnt-flex tngnt-flex-col tngnt-items-center tngnt-justify-center tngnt-relative tngnt-w-[100%] sm:tngnt-w-[100%] tngnt-m-auto">
				<Question
					question={data.questionList[currentQuestion]}
					aiConfig={data.ai_selection_config}
					aiType={data.ai_selection_type}
					backBtnConfig={data?.backBtn_config ?? null}
					handleBackBtn={handleBackBtn}
					userResponse={userResponse}
					updateUserResponse={updateUserResponse}
					sessionID={sessionId}
					saveAppEvent={saveAppEvent}
					questionIndex={currentQuestion}
					quiz={data.quiz}
					params={{ formId: container?.dataset?.formid, shopId: container?.dataset?.shopid }}
					disableAutoScroll={data?.disableAutoScroll ?? false}
					settings={data?.settings}
					customSelfieTags={data?.customSelfieTags}
					data={data}
					customerEmail={customerEmail}
				/>
				{quizSubmitPending && <Loader />}
			</div>
			{!data.hidePoweredBy && <p className='tangent_global_style tangent_global_style_custom sm:tngnt-pt-32 tngnt-pt-16 sm:tngnt-pb-32 tngnt-pb-16 tngnt-text-center tngnt-text-[12px] tngnt-text-[#888]'>{t("poweredBy")} <a href={`https://www.tangent.ai/${utmSource ? utmSource : ''}`} target='_blank' className='tngnt-underline tngnt-text-[#ec5e5e]'>Tangent.ai</a></p >}
		</>
	)
}

export default Quiz
